/**
 * GSAP + ScrollTrigger animation system.
 *
 * Lazy-loads GSAP core + ScrollTrigger on first use (~25 KB gzipped).
 * Provides:
 *   1. A declarative data-attribute API  →  data-animate="fadeUp"
 *   2. Programmatic helpers              →  animate.parallax(el, opts)
 *
 * ─── DATA-ATTRIBUTE USAGE ────────────────────────────────────────
 *
 *  <div data-animate="fadeUp">              Fade + slide up on scroll
 *  <div data-animate="fadeDown">            Fade + slide down on scroll
 *  <div data-animate="fadeLeft">            Fade + slide from left
 *  <div data-animate="fadeRight">           Fade + slide from right
 *  <div data-animate="fadeIn">              Simple fade in
 *  <div data-animate="scaleUp">             Scale from 0.85 → 1
 *  <div data-animate="stagger" data-animate-stagger=".card">
 *        Stagger children matched by selector
 *
 *  Optional modifiers (data attributes on the same element):
 *    data-animate-delay="0.2"        Delay in seconds
 *    data-animate-duration="0.8"     Duration in seconds
 *    data-animate-distance="60"      Translate distance in px
 *    data-animate-start="top 85%"    ScrollTrigger start position
 *    data-animate-stagger="0.15"     Stagger timing (or child selector)
 *    data-animate-scrub="true"       Scrub animation to scroll position
 *
 *  Parallax images:
 *    <div data-parallax>                    Default: ±80px travel
 *    <div data-parallax="-120">             Custom travel distance
 *    <img data-parallax="60">               Works on any element
 *
 * ─── PROGRAMMATIC API ────────────────────────────────────────────
 *
 *  import { initAnimations, animate } from './js/animations';
 *
 *  // Auto-scan the DOM for data-animate / data-parallax attributes:
 *  initAnimations();
 *
 *  // Or animate manually:
 *  animate.fadeUp('.my-cards', { stagger: 0.12, distance: 80 });
 *  animate.parallax('.hero-img', { distance: -120 });
 *  animate.timeline('.section', (tl) => {
 *      tl.from('.title', { y: 40, opacity: 0 })
 *        .from('.desc',  { y: 30, opacity: 0 }, '-=0.3');
 *  });
 */

let gsapCore = null;
let ScrollTriggerPlugin = null;

/**
 * Lazy-load GSAP + ScrollTrigger. Cached after first call.
 */
async function loadGSAP() {
  if (gsapCore) return { gsap: gsapCore, ScrollTrigger: ScrollTriggerPlugin };

  const [{ gsap }, { ScrollTrigger }] = await Promise.all([
    import('gsap'),
    import('gsap/ScrollTrigger'),
  ]);

  gsap.registerPlugin(ScrollTrigger);

  // Sensible global defaults
  gsap.defaults({
    ease: 'power3.out',
    duration: 1,
  });

  gsapCore = gsap;
  ScrollTriggerPlugin = ScrollTrigger;

  return { gsap, ScrollTrigger };
}

// ─── Preset definitions ──────────────────────────────────────────

const PRESETS = {
  fadeUp:    { y: (d) =>  d, opacity: 0 },
  fadeDown:  { y: (d) => -d, opacity: 0 },
  fadeLeft:  { x: (d) => -d, opacity: 0 },
  fadeRight: { x: (d) =>  d, opacity: 0 },
  fadeIn:    { opacity: 0 },
  scaleUp:   { scale: 0.85, opacity: 0 },
};

function resolvePreset(name, distance) {
  const preset = PRESETS[name];
  if (!preset) return null;

  const resolved = {};
  for (const [key, value] of Object.entries(preset)) {
    resolved[key] = typeof value === 'function' ? value(distance) : value;
  }
  return resolved;
}

// ─── Data-attribute auto-init ────────────────────────────────────

/**
 * Vanilla scroll-follow — runs without GSAP. Moves [data-scroll-follow]
 * elements vertically as their trigger element scrolls through the viewport.
 * Movement is symmetric around center: y = -distance/2 when trigger top hits
 * viewport bottom, y = +distance/2 when trigger bottom hits viewport top.
 * Same scroll-fraction → same position on every page, regardless of where
 * the block sits in the document.
 */
function initScrollFollow(scope = document) {
  const els = scope.querySelectorAll('[data-scroll-follow]');
  if (!els.length) return;

  const observers = [];

  els.forEach((el) => {
    const distance = parseFloat(el.dataset.scrollFollow) || 200;
    const triggerSel = el.dataset.scrollFollowTrigger;
    const trigger = triggerSel
      ? el.closest(triggerSel) || el.parentElement
      : el.parentElement || el;
    if (!trigger) return;

    let isVisible = false;
    let rafId = null;

    function update() {
      rafId = null;
      const rect = trigger.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const total = rect.height + vh;
      const progress = total > 0 ? 1 - (rect.top + rect.height) / total : 0.5;
      const clamped = Math.max(0, Math.min(1, progress));
      const y = (clamped - 0.5) * distance;
      el.style.transform = `translate3d(0, ${y}px, 0)`;
    }

    function onScroll() {
      if (!isVisible || rafId !== null) return;
      rafId = requestAnimationFrame(update);
    }

    // Only listen to scroll while the trigger is near the viewport
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        isVisible = entry.isIntersecting;
        if (isVisible) update();
      });
    }, { rootMargin: '200px 0px' });

    io.observe(trigger);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();

    observers.push(io);
  });
}

// Auto-init scroll-follow on DOMContentLoaded — independent of GSAP
if (typeof window !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initScrollFollow());
  } else {
    initScrollFollow();
  }
}

/**
 * Scan the DOM for [data-animate] and [data-parallax] elements
 * and wire up GSAP animations. Safe to call multiple times.
 */
export async function initAnimations(scope = document) {
  // Skip animations entirely on mobile — don't load GSAP, keep content
  // visible for better SEO, Speed Index & CLS scores.
  if (window.innerWidth < 768) return;

  const animateEls = scope.querySelectorAll('[data-animate]');
  const parallaxEls = scope.querySelectorAll('[data-parallax]');

  if (!animateEls.length && !parallaxEls.length) return;

  const { gsap } = await loadGSAP();

  // ── Animate elements ───────────────────────────────────────
  animateEls.forEach((el) => {
    const presetName = el.dataset.animate;
    const delay      = parseFloat(el.dataset.animateDelay) || 0;
    const duration   = parseFloat(el.dataset.animateDuration) || 0.8;
    const distance   = parseFloat(el.dataset.animateDistance) || 50;
    const start      = el.dataset.animateStart || 'top 85%';
    const scrub      = el.dataset.animateScrub === 'true';

    // Stagger children
    if (presetName === 'stagger') {
      const childSel = el.dataset.animateStagger || ':scope > *';
      const staggerVal = parseFloat(el.dataset.animateStaggerDelay) || 0.12;
      const children = el.querySelectorAll(childSel);

      // Set initial hidden state via GSAP (not CSS alone) so values are in sync
      gsap.set(children, { y: distance, opacity: 0 });

      let hasPlayed = false;

      function playStagger() {
        hasPlayed = true;
        gsap.to(children, {
          y: 0,
          opacity: 1,
          duration,
          stagger: staggerVal,
          delay,
          ease: 'power2.out',
        });
      }

      // Use IntersectionObserver instead of ScrollTrigger for stagger.
      // Unlike ScrollTrigger, IntersectionObserver does NOT snapshot the
      // element position at init — it observes real-time DOM position.
      // This means lazy images loading above and shifting the layout
      // never affect when the animation fires.
      //
      // rootMargin '0px 0px -15% 0px' = shrink observation area 15% from
      // the bottom → fires when element top enters bottom 85% of viewport
      // = equivalent to ScrollTrigger's 'top 85%'.
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !hasPlayed) {
              playStagger();
              observer.disconnect();
            }
          });
        },
        { rootMargin: '0px 0px -15% 0px', threshold: 0 }
      );

      observer.observe(el);

      // Allow external re-trigger (e.g. tab switching)
      el._replayStagger = playStagger;

      return;
    }

    const fromVars = resolvePreset(presetName, distance);
    if (!fromVars) return;

    const scrollTrigger = {
      trigger: el,
      start,
      scrub: scrub ? 1 : false,
      once: !scrub,
    };

    // Split opacity + movement for a smoother feel
    const hasMovement = 'y' in fromVars || 'x' in fromVars || 'scale' in fromVars;
    if ('opacity' in fromVars && hasMovement && !scrub) {
      // Quick opacity fade
      gsap.fromTo(el,
        { opacity: 0 },
        {
          opacity: 1,
          duration: duration * 0.35,
          delay,
          ease: 'power1.out',
          scrollTrigger,
        }
      );

      // Slower movement
      const moveFrom = { ...fromVars };
      delete moveFrom.opacity;
      const moveTo = {};
      if ('y' in moveFrom) moveTo.y = 0;
      if ('x' in moveFrom) moveTo.x = 0;
      if ('scale' in moveFrom) moveTo.scale = 1;

      gsap.fromTo(el, moveFrom, {
        ...moveTo,
        duration,
        delay,
        ease: 'power3.out',
        scrollTrigger: { ...scrollTrigger },
      });
    } else {
      // Fallback: single tween (scrub, or opacity-only, or movement-only)
      const toVars = {};
      if ('y' in fromVars) toVars.y = 0;
      if ('x' in fromVars) toVars.x = 0;
      if ('opacity' in fromVars) toVars.opacity = 1;
      if ('scale' in fromVars) toVars.scale = 1;

      gsap.fromTo(el, fromVars, {
        ...toVars,
        duration,
        delay,
        scrollTrigger,
      });
    }
  });

  // ── Parallax elements ──────────────────────────────────────
  parallaxEls.forEach((el) => {
    const distance = parseFloat(el.dataset.parallax) || -80;

    gsap.fromTo(el,
      { y: -distance },
      {
        y: distance,
        ease: 'none',
        scrollTrigger: {
          trigger: el.parentElement || el,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 1,
        },
      }
    );
  });

}

// ─── Programmatic helpers ────────────────────────────────────────

export const animate = {
  /**
   * Fade + slide up. Works on a single element or selector.
   */
  async fadeUp(target, opts = {}) {
    const { gsap } = await loadGSAP();
    const { distance = 50, stagger = 0, start = 'top 85%', ...rest } = opts;

    return gsap.from(target, {
      y: distance,
      opacity: 0,
      stagger,
      ...rest,
      scrollTrigger: {
        trigger: typeof target === 'string' ? target : (Array.isArray(target) ? target[0] : target),
        start,
        once: true,
        ...rest.scrollTrigger,
      },
    });
  },

  /**
   * Parallax an element tied to scroll.
   */
  async parallax(target, opts = {}) {
    const { gsap } = await loadGSAP();
    const { distance = -80, ...rest } = opts;
    const trigger = rest.trigger || (typeof target === 'string' ? target : target);

    return gsap.fromTo(target,
      { y: -distance },
      {
        y: distance,
        ease: 'none',
        ...rest,
        scrollTrigger: {
          trigger,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 1,
          ...rest.scrollTrigger,
        },
      }
    );
  },

  /**
   * Create a scroll-triggered timeline for complex sequences.
   *
   * Usage:
   *   animate.timeline('.section', (tl) => {
   *     tl.from('.title', { y: 40, opacity: 0 })
   *       .from('.desc',  { y: 30, opacity: 0 }, '-=0.3');
   *   });
   */
  async timeline(trigger, builder, opts = {}) {
    const { gsap } = await loadGSAP();
    const { start = 'top 80%', ...rest } = opts;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger,
        start,
        once: true,
        ...rest.scrollTrigger,
      },
      ...rest,
    });

    builder(tl);
    return tl;
  },

  /**
   * Direct access to loaded gsap + ScrollTrigger for advanced use.
   */
  async getGSAP() {
    return loadGSAP();
  },
};

export default animate;
